import type {
  TCalculatorFields,
  TCalculatorForm,
  TFirstSplit,
  TSecondSplit,
} from 'constants/types';

export const DEFAULT_POKEMON_1: Omit<TFirstSplit, 'pokemon1' | 'currenthp1'> = {
  level1: 100,
  gender1: undefined,
  dvatk1: 15,
  dvdef1: 15,
  dvspc1: 15,
  dvspeed1: 15,
  evatk1: 0,
  evdef1: 0,
  evhp1: 0,
  evspatk1: 0,
  evspdef1: 0,
  evspeed1: 0,
  ivatk1: 31,
  ivdef1: 31,
  ivhp1: 31,
  ivspatk1: 31,
  ivspdef1: 31,
  ivspeed1: 31,
  modatk1: 0,
  moddef1: 0,
  modspatk1: 0,
  modspdef1: 0,
  modspeed1: 0,
  nature1: undefined,
  ability1: undefined,
  item1: undefined,
  status1: 'none',
  cannonade1: false,
  isAuroraVeil1: false,
  isBattery1: false,
  isDynamaxed1: false,
  isForesight1: false,
  isFriendGuard1: false,
  isHelpingHand1: false,
  isLightScreen1: false,
  isProtected1: false,
  isReflect1: false,
  isSeeded1: false,
  isSR1: false,
  isSwitching1: false,
  isTailwind1: false,
  spikes1: 0,
  steelsurge1: false,
  volcalith1: false,
  vinelash1: false,
  wildfire1: false,
  move1_1: 1,
  move1_crit1: false,
  move1_z1: false,
  move2_1: 1,
  move2_crit1: false,
  move2_z1: false,
  move3_1: 1,
  move3_crit1: false,
  move3_z1: false,
  move4_1: 1,
  move4_crit1: false,
  move4_z1: false,
};

export const DEFAULT_POKEMON_2: Omit<TSecondSplit, 'pokemon2' | 'currenthp2'> = {
  level2: 100,
  gender2: undefined,
  dvatk2: 15,
  dvdef2: 15,
  dvspc2: 15,
  dvspeed2: 15,
  evatk2: 0,
  evdef2: 0,
  evhp2: 0,
  evspatk2: 0,
  evspdef2: 0,
  evspeed2: 0,
  ivatk2: 31,
  ivdef2: 31,
  ivhp2: 31,
  ivspatk2: 31,
  ivspdef2: 31,
  ivspeed2: 31,
  modatk2: 0,
  moddef2: 0,
  modspatk2: 0,
  modspdef2: 0,
  modspeed2: 0,
  nature2: undefined,
  ability2: undefined,
  item2: undefined,
  status2: 'none',
  cannonade2: false,
  isAuroraVeil2: false,
  isBattery2: false,
  isDynamaxed2: false,
  isForesight2: false,
  isFriendGuard2: false,
  isHelpingHand2: false,
  isLightScreen2: false,
  isProtected2: false,
  isReflect2: false,
  isSeeded2: false,
  isSR2: false,
  isSwitching2: false,
  isTailwind2: false,
  spikes2: 0,
  steelsurge2: false,
  vinelash2: false,
  volcalith2: false,
  wildfire2: false,
  move1_2: 1,
  move1_crit2: false,
  move1_z2: false,
  move2_2: 1,
  move2_crit2: false,
  move2_z2: false,
  move3_2: 1,
  move3_crit2: false,
  move3_z2: false,
  move4_2: 1,
  move4_crit2: false,
  move4_z2: false,
};

export const DEFAULT_VALUES: Omit<TCalculatorForm, 'calculatorGen' | 'pokemon1' | 'pokemon2'> = {
  gameType: 'Singles',
  isGravity: false,
  terrain: 'None',
  weather: 'None',
  currenthp1: 0,
  currenthp2: 0,
  ...DEFAULT_POKEMON_1,
  ...DEFAULT_POKEMON_2,
};

export const SIDE_FIELD: { [key in keyof Omit<TCalculatorFields, 'spikes'>]: string } = {
  isDynamaxed: 'Dynamax',
  isLightScreen: 'Light Screen',
  isReflect: 'Reflect',
  isProtected: 'Protect',
  isSR: 'Stealth Rock',
  isSeeded: 'Leech Seed',
  isTailwind: 'Tailwind',
  isForesight: 'Foresight',
  isFriendGuard: 'Friend Guard',
  isHelpingHand: 'Helping Hand',
  cannonade: 'Cannonade',
  isAuroraVeil: 'Aurora Veil',
  isBattery: 'Battery',
  steelsurge: 'Steelsurge',
  vinelash: 'Vinelash',
  volcalith: 'Volcalith',
  wildfire: 'Wildfire',
  isSwitching: 'Switching out',
};

export const TERRAIN = ['None', 'Electric', 'Grassy', 'Misty', 'Psychic'];

export const WEATHER = [
  'None',
  'Sun',
  'Rain',
  'Sand',
  'Hail',
  'Harsh Sunshine',
  'Heavy Rain',
  'Strong Winds',
];

export const WEATHER_EXCLUSIONS: { [key: string]: string[] } = {
  '1': ['Harsh Sunshine', 'Heavy Rain', 'Strong Winds', 'Hail', 'Sun', 'Rain', 'Sand'],
  '2': ['Harsh Sunshine', 'Heavy Rain', 'Strong Winds', 'Hail'],
  '3': ['Harsh Sunshine', 'Heavy Rain', 'Strong Winds'],
  '4': ['Harsh Sunshine', 'Heavy Rain', 'Strong Winds'],
  '5': ['Harsh Sunshine', 'Heavy Rain', 'Strong Winds'],
  '6': [],
  '7': [],
  '8': [],
};

export const FIELD_EXCLUSIONS: { [key: string]: string[] } = {
  '1': [
    'isSR',
    'isTailwind',
    'steelsurge',
    'vinelash',
    'volcalith',
    'wildfire',
    'cannonade',
    'isAuroraVeil',
    'isBattery',
    'isForesight',
    'isFriendGuard',
    'isHelpingHand',
    'isProtected',
    'isDynamaxed',
    'isSwitching',
  ],
  '2': [
    'isSR',
    'isTailwind',
    'steelsurge',
    'vinelash',
    'volcalith',
    'wildfire',
    'cannonade',
    'isAuroraVeil',
    'isBattery',
    'isFriendGuard',
    'isHelpingHand',
    'isDynamaxed',
  ],
  '3': [
    'isSR',
    'isTailwind',
    'steelsurge',
    'vinelash',
    'volcalith',
    'wildfire',
    'cannonade',
    'isAuroraVeil',
    'isBattery',
    'isFriendGuard',
    'isDynamaxed',
  ],
  '4': [
    'steelsurge',
    'vinelash',
    'volcalith',
    'wildfire',
    'cannonade',
    'isAuroraVeil',
    'isBattery',
    'isFriendGuard',
    'isDynamaxed',
  ],
  '5': [
    'steelsurge',
    'vinelash',
    'volcalith',
    'wildfire',
    'cannonade',
    'isAuroraVeil',
    'isBattery',
    'isDynamaxed',
  ],
  '6': [
    'steelsurge',
    'vinelash',
    'volcalith',
    'wildfire',
    'cannonade',
    'isAuroraVeil',
    'isBattery',
    'isDynamaxed',
  ],
  '7': ['steelsurge', 'vinelash', 'volcalith', 'wildfire', 'cannonade', 'isDynamaxed'],
  '8': [],
};

export enum GenderCalc {
  'FEMALE' = 'F',
  'MALE' = 'M',
  'NEUTRAL' = 'N',
}

export const STATUS_EFFECTS = [
  { key: 'healthy', text: 'Healthy', value: 'none' },
  { key: 'psn', text: 'Poisoned', value: 'psn' },
  { key: 'par', text: 'Paralyzed', value: 'par' },
  { key: 'slp', text: 'Asleep', value: 'slp' },
  { key: 'brn', text: 'Burned', value: 'brn' },
  { key: 'tox', text: 'Badly poisoned', value: 'tox' },
  { key: 'frz', text: 'Frozen', value: 'frozen' },
];

export const FORBIDDEN_ITEMS = [
  'Abomasite',
  'Absolite',
  'Aerodactylite',
  'Aggronite',
  'Alakazite',
  'Altarianite',
  'Ampharosite',
  'Audinite',
  'Banettite',
  'Beedrillite',
  'Blastoisinite',
  'Blazikenite',
  'Cameruptite',
  'Charizardite X',
  'Charizardite Y',
  'Diancite',
  'Galladite',
  'Garchompite',
  'Gardevoirite',
  'Gengarite',
  'Glaltite',
  'Gyaradosite',
  'Heracronite',
  'Houndoominite',
  'Kangaskhanite',
  'Latiasite',
  'Latiosite',
  'Lopunnite',
  'Lucarionite',
  'Manectite',
  'Mawilite',
  'Medichamite',
  'Metagrossite',
  'Mewtwonite X',
  'Mewtwonite Y',
  'Pidgeotite',
  'Pinsirite',
  'Sablenite',
  'Salamencite',
  'Sceptilite',
  'Scizorite',
  'Sharpedonite',
  'Slowbronite',
  'Steelixite',
  'Swampertite',
  'Tyranitarite',
  'Venusaurite',
  'Amulet Coin',
  'Soothe Bell',
  'PSN Cure Berry',
  'Exp. Share',
];
